import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { useEditProjectMutation, useGetClientsQuery, useGetLibrariesQuery, useGetProjectQuery } from '../../../generated/graphql';
import { FormAutoComplete, FormField } from '../../form';
import styles from './EditProjectDialog.module.css';

type EditProjectDialogProps = {
    projectId: string,
    onClose: () => void,
    visible: boolean,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const EditProjectDialog = (props: EditProjectDialogProps) => {
    const { projectId, style, visible, onClose } = props;
    const [editProject, editProjectResult] = useEditProjectMutation();
    const [searchClients, setSearchClients] = useState('');
    const [searchText, setSearchText] = useState('');
    const [projectSubName, setProjectSubName] = useState('');
    const [validationError, setValidationError] = useState('');
    const [initialized, setInitialized] = useState(false);

    const project = useGetProjectQuery({
        variables: { id: projectId },
        skip: !projectId,
    });

    const clients = useGetClientsQuery({
        variables: { contains: searchClients }
    });

    const libraryData = useGetLibrariesQuery();

    // Pre-fill fields with current project values once data is loaded
    useEffect(() => {
        if (!initialized && project.data?.Project) {
            const p = project.data.Project;
            if (p.client?.name) {
                setSearchClients(p.client.name);
                // Refetch libraries for the current client
                libraryData.refetch({
                    clientId: p.clientId,
                    contains: ''
                });
            }
            if (p.library?.name) {
                setSearchText(p.library.name);
            }
            if (p.projectSubName) {
                setProjectSubName(p.projectSubName);
            }
            setInitialized(true);
        }
    }, [project.data, initialized]);

    const handleClientChanged = (value: string) => {
        setSearchClients(value);
        setSearchText('');
        const client = clients.data?.ClientList?.nodes?.filter(l => l?.name === value)[0];
        if (client) libraryData.refetch({
            clientId: client.id,
            contains: ''
        });
    };

    const handleSubmit = () => {
        const selected = libraryData.data?.LibraryList?.nodes?.filter(l => l?.name === searchText)[0];
        const selectedClient = clients.data?.ClientList?.nodes?.filter(l => l?.name === searchClients)[0];

        if (!searchClients || !searchText) {
            setValidationError('Please fill all of the following fields.');
            return;
        }
        if (!selectedClient) {
            setValidationError('Please Select A Valid Client!');
            return;
        }
        if (!selected) {
            setValidationError('Please Select A Valid Library!');
            return;
        }
        setValidationError('');
        editProject({
            variables: {
                projectId,
                clientId: selectedClient?.id,
                libraryId: selected?.id,
                projectSubName
            }
        })
            .then(() => { onClose(); })
            .catch(() => {});
    };

    return (
        <DialogBase
            title="Edit Project"
            type="OkCancel"
            isLoading={editProjectResult.loading || project.loading}
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            validationError={validationError}
            className={styles.NewProjectDialog}
            style={style}
            data-testid="NewProjectDialog">
            <FormAutoComplete
                label="Client"
                name="client"
                value={searchClients}
                onChange={handleClientChanged}
                options={(clients.data?.ClientList?.nodes?.map(c => c?.name) || []).filter((n): n is string => !!n)}
            />
            <FormAutoComplete
                label="Library"
                name="library"
                value={searchText}
                onChange={setSearchText}
                options={(libraryData.data?.LibraryList?.nodes?.map(l => l?.name) || []).filter((n): n is string => !!n)}
            />
            <FormField label="Sub Project Name" name="projectSubName" value={projectSubName} onChange={setProjectSubName} />
        </DialogBase>
    );
};

export default EditProjectDialog;